import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectNotifications,
  selectNotificationsLoading,
  selectNotificationsError,
} from "../../store/notification-slice";

import { Bell, Check, Trash2, RefreshCw, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  clearAllNotifications,
  deleteNotification,
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../store/apis/notification-thunk";

const NotificationPage = () => {
  const notifications = useSelector(selectNotifications);
  const isLoading = useSelector(selectNotificationsLoading);
  const error = useSelector(selectNotificationsError);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all"); // all, unread, read

  useEffect(() => {
    // making request
    console.log("MAKING REQUEST");
    dispatch(fetchNotifications());
  }, []);

  // Handle notification click
  const handleNotificationClick = (notification) => {
    dispatch(markNotificationAsRead(notification.id));

    // Navigate based on notification type if needed
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Handle delete notification
  const handleDeleteNotification = (e, notificationId) => {
    e.stopPropagation();
    dispatch(deleteNotification(notificationId));
  };

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsRead());
  };

  // Handle clear all notifications
  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear all notifications?")) {
      dispatch(clearAllNotifications());
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter((notification) => {
    if (activeFilter === "unread") return !notification.read;
    if (activeFilter === "read") return notification.read;
    return true;
  });

  // Refresh notifications
  const handleRefresh = () => {
    dispatch(fetchNotifications());
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="bg-teal rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-teal p-4 sm:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-700">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-0">
            Notifications
          </h1>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              onClick={handleRefresh}
              className="flex items-center py-2 px-3 text-sm rounded-lg bg-yellow text-white transition-colors duration-150"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>

            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center py-2 px-3 text-sm rounded-lg bg-yellow text-white transition-colors duration-150"
            >
              <Check className="w-4 h-4 mr-2" />
              Mark All Read
            </button>

            <button
              onClick={handleClearAll}
              className="flex items-center py-2 px-3 text-sm rounded-lg bg-yellow text-white transition-colors duration-150"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-teal p-4 border-b border-gray-700">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveFilter("all")}
              className={`py-1 px-3 text-sm rounded-lg transition-colors duration-150 ${
                activeFilter === "all"
                  ? "bg-yellow text-[#0C1B22] font-medium"
                  : "bg-buttonGradient text-white hover:bg-[#4F5F75]"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveFilter("unread")}
              className={`py-1 px-3 text-sm rounded-lg transition-colors duration-150 ${
                activeFilter === "unread"
                  ? "bg-yellow text-[#0C1B22] font-medium"
                  : "bg-buttonGradient text-white hover:bg-[#4F5F75]"
              }`}
            >
              Unread
            </button>
            <button
              onClick={() => setActiveFilter("read")}
              className={`py-1 px-3 text-sm rounded-lg transition-colors duration-150 ${
                activeFilter === "read"
                  ? "bg-yellow text-[#0C1B22] font-medium"
                  : "bg-buttonGradient text-white hover:bg-[#4F5F75]"
              }`}
            >
              Read
            </button>
          </div>
        </div>

        {/* Notifications content */}
        <div className="p-4">
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 text-red-400 p-4 rounded-lg flex items-center mb-4">
              <XCircle className="w-5 h-5 mr-2" />
              <p>Failed to load notifications. Please try again.</p>
            </div>
          )}

          {!isLoading && !error && filteredNotifications.length === 0 && (
            <div className="bg-teal rounded-lg p-8 text-center">
              <Bell className="w-12 h-12 mx-auto text-gray-500 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                No notifications
              </h3>
              <p className="text-gray-400">
                {activeFilter === "all"
                  ? "You don't have any notifications yet."
                  : activeFilter === "unread"
                  ? "You don't have any unread notifications."
                  : "You don't have any read notifications."}
              </p>
            </div>
          )}

          {!isLoading && filteredNotifications.length > 0 && (
            <div className="space-y-2">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start p-4 rounded-lg cursor-pointer transition-colors duration-150 ${
                    !notification.read
                      ? "bg-teal hover:bg-[#3D4B5C]"
                      : "bg-[#0C1B22] hover:bg-[#162A3A]"
                  }`}
                >
                  {/* Notification icon */}
                  <div
                    className={`flex-shrink-0 rounded-full p-3 mr-4 ${
                      getNotificationTypeStyle(notification.type).bgColor
                    }`}
                  >
                    {getNotificationTypeStyle(notification.type).icon}
                  </div>

                  {/* Notification content */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p
                        className={`text-sm ${
                          !notification.read
                            ? "font-medium text-white"
                            : "text-gray-300"
                        }`}
                      >
                        {notification.title}
                      </p>
                      <div className="flex items-center ml-2">
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-yellow mr-2"></div>
                        )}
                        <button
                          onClick={(e) =>
                            handleDeleteNotification(e, notification.id)
                          }
                          className="text-gray-400 hover:text-red-400 transition-colors duration-150"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {notification.message}
                    </p>
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-gray-500">
                        {format(
                          new Date(notification.createdAt),
                          "MMM d, yyyy 'at' h:mm a"
                        )}
                      </p>
                      {notification.link && (
                        <button className="text-xs text-yellow hover:text-yellow-dark">
                          View details
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to get notification type styles
const getNotificationTypeStyle = (type) => {
  switch (type) {
    case "deposit":
      return {
        bgColor: "bg-green-500/20",
        icon: <Bell size={20} className="text-green-500" />,
      };
    case "withdrawal":
      return {
        bgColor: "bg-red-500/20",
        icon: <Bell size={20} className="text-red-500" />,
      };
    case "bet":
      return {
        bgColor: "bg-blue-500/20",
        icon: <Bell size={20} className="text-blue-500" />,
      };
    case "win":
      return {
        bgColor: "bg-yellow-500/20",
        icon: <Bell size={20} className="text-yellow-500" />,
      };
    default:
      return {
        bgColor: "bg-gray-500/20",
        icon: <Bell size={20} className="text-gray-500" />,
      };
  }
};

export default NotificationPage;
